import React from 'react';
import { teamMembers } from '../mock';
import { Mail, Linkedin, Droplet, Award, Heart, Users } from 'lucide-react';

const Team = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-rose-500 to-red-500 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">About Arihant Milk Products</h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Purity at Scale - Asia's largest private dairy network committed to absolute purity
          </p>
        </div>
      </div>

      {/* About Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Main About Content */}
        <div className="bg-white rounded-2xl shadow-lg p-10 mb-12">
          <h2 className="text-4xl font-bold text-rose-900 mb-6 text-center">Purity at Scale</h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            At Arihant Milk Products, we operate on a philosophy most find impossible: to be the largest and the purest at the same time.
          </p>
          <p className="text-gray-700 text-lg leading-relaxed">
            We run Asia's largest private dairy network. This scale gives us unmatched reach, but it's our guiding belief that gives us our purpose.
          </p>
        </div>

        {/* An Offering of Purity */}
        <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl shadow-lg p-10 mb-12 border border-rose-100">
          <div className="flex items-center mb-6">
            <div className="w-16 h-16 bg-rose-600 rounded-full flex items-center justify-center mr-4">
              <Heart className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-rose-900">An Offering of Purity</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed mb-4">
            We don't just process milk. We create our products with a singular, guiding thought: every batch is an offering, devoted to a higher purpose.
          </p>
          <p className="text-gray-700 text-lg leading-relaxed mb-4">
            This is not a marketing slogan; it is our entire business model. In a world of shortcuts, we believe purity is sacred. We follow pure methods to ensure zero adulteration.
          </p>
          <p className="text-gray-700 text-lg leading-relaxed font-semibold">
            Because we view our work as a devotion, our standard is absolute.
          </p>
        </div>

        {/* Masters of the Essentials */}
        <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl shadow-lg p-10 mb-12 border border-rose-100">
          <div className="flex items-center mb-6">
            <div className="w-16 h-16 bg-rose-600 rounded-full flex items-center justify-center mr-4">
              <Award className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-rose-900">Masters of the Essentials</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Our focus is on the core of dairy, perfected through our sacred philosophy. We specialize in:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex items-center space-x-3">
              <Droplet className="h-6 w-6 text-rose-600" />
              <span className="text-gray-700 text-lg font-medium">Milk</span>
            </div>
            <div className="flex items-center space-x-3">
              <Droplet className="h-6 w-6 text-rose-600" />
              <span className="text-gray-700 text-lg font-medium">Pure Ghee</span>
            </div>
            <div className="flex items-center space-x-3">
              <Droplet className="h-6 w-6 text-rose-600" />
              <span className="text-gray-700 text-lg font-medium">Cow Ghee</span>
            </div>
            <div className="flex items-center space-x-3">
              <Droplet className="h-6 w-6 text-rose-600" />
              <span className="text-gray-700 text-lg font-medium">Butter</span>
            </div>
            <div className="flex items-center space-x-3">
              <Droplet className="h-6 w-6 text-rose-600" />
              <span className="text-gray-700 text-lg font-medium">Skimmed Milk Powder</span>
            </div>
            <div className="flex items-center space-x-3">
              <Droplet className="h-6 w-6 text-rose-600" />
              <span className="text-gray-700 text-lg font-medium">Dairy Whitener</span>
            </div>
            <div className="flex items-center space-x-3">
              <Droplet className="h-6 w-6 text-rose-600" />
              <span className="text-gray-700 text-lg font-medium">Sweets</span>
            </div>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed">
            We have built our network by mastering these essentials without compromise.
          </p>
        </div>

        {/* Join Our Network */}
        <div className="bg-gradient-to-r from-rose-500 to-red-500 rounded-2xl shadow-lg p-10 mb-16 text-white text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center">
              <Users className="h-10 w-10 text-rose-600" />
            </div>
          </div>
          <h2 className="text-3xl font-bold mb-4">Join Our Network</h2>
          <p className="text-lg leading-relaxed mb-4">
            Our network is vast, but our vision is bigger. We are actively looking for partners—distributors, retailers, and suppliers—who share our non-negotiable commitment to purity.
          </p>
          <p className="text-xl font-semibold">
            Join us. Let's nourish the world, purely.
          </p>
        </div>

        {/* Our Team Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-rose-900 mb-4">Our Team</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Meet the passionate people behind Arihant Milk Products who work tirelessly to bring you the best
          </p>
        </div>

      {/* Team Members */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {teamMembers.map(member => (
            <div
              key={member.id}
              className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden hover:-translate-y-1"
            >
              <div className="h-64 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden">
                {member.image ? (
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover object-center"
                  />
                ) : (
                  <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center">
                    <span className="text-5xl font-bold text-rose-600">
                      {member.name.charAt(0)}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                <p className="text-rose-600 font-medium text-sm mb-4">{member.role}</p>
                <p className="text-gray-600 text-sm mb-4">{member.bio}</p>
                <div className="flex space-x-3">
                  <button className="p-2 text-gray-600 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors duration-200">
                    <Mail className="h-5 w-5" />
                  </button>
                  <button className="p-2 text-gray-600 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors duration-200">
                    <Linkedin className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Join Us Section */}
        <div className="mt-16 bg-white rounded-2xl shadow-md p-12 text-center">
          <h2 className="text-3xl font-bold text-rose-900 mb-4">Join Our Team</h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            We're always looking for passionate individuals who share our commitment to quality and tradition. Be part of our journey!
          </p>
          <button className="bg-rose-500 hover:bg-rose-600 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200">
            View Open Positions
          </button>
        </div>
      </div>
    </div>
  );
};

export default Team;
